import React from "react";
import { motion } from "framer-motion";
import { Check, Mail, Star } from "lucide-react";
import { PageHero } from "../components/Shared";

const CONTACT_EMAIL = "watchcwf@gmail.com";

const currentSponsors = [
  { name: "East Texas Food Bank", logo: "https://customer-assets.emergentagent.com/job_wrestling-federation/artifacts/9rvmtb71_ETFB%20Logo.jpg", url: "https://www.easttexasfoodbank.org" },
  { name: "True Vine Brewing Company", logo: null, url: "https://www.truevinebrewing.com/" },
  { name: "Gold Label Boxing", logo: null, url: "https://www.goldlabelboxing.com" },
  { name: "Shehds Lighting", logo: null, url: "https://www.shehds.com" },
  { name: "MaestroDMX", logo: null, url: "https://www.maestrodmx.com" },
];

const tiers = [
  {
    name: "Founding Supporter",
    tagline: "Help launch East Texas' premier sports entertainment property.",
    note: "Limited — only 20 Founding Supporter positions available.",
    benefits: [
      { title: "Brand Visibility", text: "Logo on sponsor signage at all six annual events and on the CWF website sponsor page." },
      { title: "Digital Exposure", text: "Two dedicated social media mentions annually plus inclusion in CWF sponsor appreciation content." },
      { title: "Live Event Benefits", text: "Four reserved seats at one designated CWF event plus live announcer recognition." },
      { title: "Community Connection", text: "Recognition as a supporter of East Texas live events, community initiatives, and a growing regional entertainment brand." },
      { title: "Founding Recognition", text: "Permanent recognition as one of only 20 Founding Supporters who helped launch CWF." },
    ],
  },
  {
    name: "Community Partner",
    tagline: "Join a growing network of local businesses building East Texas wrestling.",
    note: "A family-friendly East Texas brand alignment.",
    benefits: [
      { title: "Brand Visibility", text: "Logo on all event posters, the CWF website sponsor page, and select marketing campaigns." },
      { title: "Live Event Presence", text: "Sponsor signage and live announcer recognition at all six annual events." },
      { title: "Digital Exposure", text: "Six dedicated social media mentions annually plus inclusion in CWF sponsor spotlight content." },
      { title: "Hospitality", text: "Four VIP tickets with reserved seating at every CWF event." },
      { title: "Community Alignment", text: "Recognition alongside CWF's charitable and community outreach initiatives." },
    ],
  },
  {
    name: "Premier Partner",
    tagline: "Category-exclusive branding and a spotlight night all your own.",
    note: "Category exclusivity — limited to four Premier Partners total.",
    featured: true,
    benefits: [
      { title: "Brand Visibility", text: "Logo on all event posters, the CWF website, digital advertising, and every marketing campaign." },
      { title: "Live Event Presence", text: "Ringside banner, ring announcer recognition, and vendor/activation space at all six annual events." },
      { title: "Spotlight Night", text: "One designated event per year where your brand is the featured partner — enhanced branding all evening." },
      { title: "Digital Exposure", text: "Minimum 12 dedicated social media posts annually plus inclusion in all event promotion campaigns." },
      { title: "Hospitality", text: "Four VIP tickets with reserved premium seating and talent meet-and-greet opportunities at every event." },
      { title: "Exclusivity", text: "Category exclusive — no competing business in your industry." },
    ],
  },
  {
    name: "Founding Partner",
    tagline: "Our flagship cornerstone partnership with title naming rights.",
    note: "Limited positions — category exclusivity guaranteed.",
    benefits: [
      { title: "Event Branding", text: "Title sponsorship naming rights on select events. Prominent in-arena signage at every show." },
      { title: "Premium Visibility", text: "Ring announcer recognition, video board placement, and event collateral branding." },
      { title: "Social Integration", text: "Year-round featured promotion across all CWF digital and social channels." },
      { title: "Hospitality", text: "Reserved premium seating + backstage VIP access for up to 4 guests per event." },
      { title: "Exclusivity", text: "Category exclusivity — no competing brand in your industry among Founding Partners." },
      { title: "Legacy Recognition", text: "Permanent recognition as a cornerstone partner who helped build CWF." },
    ],
  },
];

const mailto = (tier) =>
  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`CWF ${tier} Sponsorship Inquiry`)}`;

export default function Sponsors() {
  return (
    <div data-testid="sponsors-page">
      <PageHero kicker="Partner With Us" title="Sponsors" image="https://customer-assets.emergentagent.com/job_wrestling-federation/artifacts/nkiznswr_Event%20Image.jpg" />

      <section className="px-6 md:px-12 lg:px-20 pb-16">
        <div className="max-w-3xl">
          <p className="font-display text-3xl sm:text-4xl uppercase tracking-tight leading-none">
            Built for the fans. <span className="text-brand">Powered by the community.</span>
          </p>
          <p className="text-zinc-400 mt-5 leading-relaxed text-lg">
            CWF events are currently free to attend — which means our sponsors are the backbone of everything we do. Choose the partnership level that fits your business and put your brand in front of a loud, loyal, growing East Texas audience.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 items-center">
            <span className="border border-brand text-brand font-mono text-xs uppercase tracking-widest px-4 py-2">All sponsorships are a one-year term</span>
            <a href={mailto("General")} data-testid="sponsors-email-link" className="text-brand hover:text-brand-hi font-mono text-sm inline-flex items-center gap-2">
              <Mail size={16} /> {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* CURRENT SPONSORS SHOWCASE */}
      <section className="px-6 md:px-12 lg:px-20 pb-20" data-testid="current-sponsors">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-8 h-[2px] bg-brand" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Proudly Backed By</span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-none mb-10">Our Sponsors</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {currentSponsors.map((s, i) => {
            const inner = s.logo ? (
              <img src={s.logo} alt={s.name} className="max-h-24 w-auto object-contain" />
            ) : (
              <span className="font-display text-2xl uppercase tracking-tight text-black text-center leading-none px-2">{s.name}</span>
            );
            return (
              <motion.div
                key={s.name}
                data-testid={`current-sponsor-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 5) * 0.08 }}
                className="bg-white h-40 flex items-center justify-center p-6 border border-zinc-800 hover:border-brand transition-colors"
              >
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noreferrer" className="flex items-center justify-center w-full h-full">{inner}</a>
                ) : inner}
              </motion.div>
            );
          })}
        </div>
        <p className="text-zinc-500 text-sm mt-6 max-w-2xl">
          CWF is proud to be supported by these outstanding partners. Interested in joining them? Explore our sponsorship levels below.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-[2px] bg-brand" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Get Involved</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-none">Sponsorship Offerings</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              data-testid={`sponsor-tier-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 2) * 0.1 }}
              className={`flex flex-col p-8 border ${t.featured ? "border-brand bg-[#141210]" : "border-zinc-800 bg-[#111]"}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-4xl uppercase leading-none tracking-tight">{t.name}</h3>
                  <p className="text-zinc-400 mt-2">{t.tagline}</p>
                </div>
                {t.featured && (
                  <span className="shrink-0 bg-brand text-black font-mono text-[10px] uppercase tracking-widest px-3 py-1 flex items-center gap-1">
                    <Star size={12} /> Popular
                  </span>
                )}
              </div>

              <span className="mt-4 w-fit font-mono text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-800 px-3 py-1">
                One-Year Term
              </span>

              <ul className="mt-6 space-y-4 flex-1">
                {t.benefits.map((b) => (
                  <li key={b.title} className="flex gap-3">
                    <Check size={18} className="text-brand shrink-0 mt-1" />
                    <div>
                      <div className="font-display text-lg uppercase tracking-wide leading-none">{b.title}</div>
                      <div className="text-zinc-400 text-sm mt-1 leading-relaxed">{b.text}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <p className="text-zinc-500 text-xs font-mono uppercase tracking-wider mt-6">{t.note}</p>

              <a
                href={mailto(t.name)}
                data-testid={`sponsor-tier-cta-${i}`}
                className={`mt-6 font-display text-xl uppercase tracking-wide px-6 py-3 text-center transition-colors ${t.featured ? "bg-brand hover:bg-brand-hi text-black" : "border-2 border-brand text-brand hover:bg-brand hover:text-black"}`}
              >
                Sponsor This Level
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="border-2 border-brand bg-[#0d0d0d] p-10 md:p-16 text-center">
          <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tight leading-none">Become A CWF Sponsor</h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">Ready to get your brand in the ring? Reach out and our team will help you find the perfect one-year partnership for your business and our community.</p>
          <a href={mailto("General")} data-testid="sponsors-contact-cta"
            className="mt-8 inline-flex items-center gap-2 bg-brand hover:bg-brand-hi text-black font-display text-2xl uppercase tracking-wide px-10 py-4 transition-colors">
            <Mail size={22} /> Email {CONTACT_EMAIL}
          </a>
        </div>
      </section>
    </div>
  );
}
